import React from 'react';
import { createPortal } from 'react-dom';
import {
    AnimatePresence,
    motion,
    PanInfo,
    useDragControls,
    useReducedMotion,
} from 'motion/react';
import './Sheet.scss';
import { useEscapeClose, useLockBodyScroll } from './useDialogEffects';

export interface SheetProps {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    ariaLabel?: string;
}

const CLOSE_OFFSET_PX = 120;
const CLOSE_VELOCITY = 600;
function Sheet({
    open,
    onClose,
    children,
    ariaLabel,
}: SheetProps): React.ReactElement | null {
    const reduced = useReducedMotion();
    const dragControls = useDragControls();

    useEscapeClose(open, onClose);
    useLockBodyScroll(open);

    const onDragEnd = React.useCallback(
        (_event: unknown, info: PanInfo): void => {
            if (
                info.offset.y > CLOSE_OFFSET_PX ||
                info.velocity.y > CLOSE_VELOCITY
            ) {
                onClose();
            }
        },
        [onClose]
    );

    if (typeof document === 'undefined') return null;

    return createPortal(
        <AnimatePresence>
            {open ? (
                <div className="rd-sheet-layer">
                    <motion.div
                        className="rd-sheet-backdrop"
                        onClick={onClose}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: reduced ? 0 : 0.2 }}
                    />
                    <motion.div
                        className="rd-sheet"
                        role="dialog"
                        aria-modal="true"
                        aria-label={ariaLabel}
                        drag="y"
                        dragListener={false}
                        dragControls={dragControls}
                        dragConstraints={{ top: 0, bottom: 0 }}
                        dragElastic={{ top: 0, bottom: 0.5 }}
                        onDragEnd={onDragEnd}
                        initial={{ y: reduced ? 0 : '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: reduced ? 0 : '100%' }}
                        transition={
                            reduced
                                ? { duration: 0 }
                                : {
                                      type: 'spring',
                                      damping: 34,
                                      stiffness: 320,
                                  }
                        }
                    >
                        <div
                            className="rd-sheet__grabber"
                            onPointerDown={e => dragControls.start(e)}
                        >
                            <span className="rd-sheet__handle" />
                        </div>
                        <button
                            type="button"
                            className="rd-sheet__close"
                            onClick={onClose}
                            aria-label="Закрыть"
                        >
                            <span className="rd-sheet__close-bar" />
                            <span className="rd-sheet__close-bar" />
                        </button>
                        <div className="rd-sheet__body">{children}</div>
                    </motion.div>
                </div>
            ) : null}
        </AnimatePresence>,
        document.body
    );
}

Sheet.defaultProps = { ariaLabel: undefined };

export default React.memo(Sheet);
